// src/components/pages/portfolios/hooks/usePortfolioDetail.js

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { toast } from 'react-toastify';
import DOMPurify from 'dompurify';
import LoginPromptToast from '../../../partials/LoginPromptToast/LoginPromptToast';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const DEFAULT_PROFILE_URL = process.env.REACT_APP_DEFAULT_PROFILE_IMAGE;

export const usePortfolioDetail = (t) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const portfolioId = params.portfolioId || params.id;
  const currentUserId = auth.user?.profile?.sub;
  const idToken = auth.user?.id_token;

  const [po, setPo] = useState(null);
  const [timeSeries, setTimeSeries] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [showRebalanceModal, setShowRebalanceModal] = useState(false);
  const [isNoData, setIsNoData] = useState(false);

  // 초기 토스트 메시지 및 스크롤 처리
  useEffect(() => {
    if (location.state?.portfolioCreated) {
      toast.success(location.state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
    if (portfolioId) window.scrollTo(0, 0);
  }, [location.state, navigate, location.pathname, portfolioId]);

  // 팔로우 상태 확인 함수
  const checkIfFollowing = useCallback(async (followerId, followingId, token) => {
    try {
      const res = await fetch(`${API_BASE_URL}/user/following/${followerId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        return data.following.some(user => user.userId === followingId);
      }
      return false;
    } catch (e) { console.error(e); return false; }
  }, []);

  // 포트폴리오 데이터 로딩
  useEffect(() => {
    if (!portfolioId) return;
    let isMounted = true;
    
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/portfolios/${portfolioId}`);
        if (!res.ok) throw new Error(t('portfolioDetail.fetchPortfolioFailed', { status: res.status }));
        const data = await res.json();
        
        if (isMounted) {
          setPo(data);
          
          // 문서 타이틀 설정
          const nickname = data.userId === 'LAZYPORT' ? 'LAZY_PORT' : (data.nickname || t('portfolioDetail.unnamed'));
          const sanitizedNickname = DOMPurify.sanitize(nickname);
          const sanitizedPoName = DOMPurify.sanitize(data.name);
          document.title = `${sanitizedNickname}님의 ${sanitizedPoName}` || t('portfolioDetail.title');

          // 팔로우 상태 체크
          if (currentUserId && idToken && data.userId && data.userId !== currentUserId) {
            const followingStatus = await checkIfFollowing(currentUserId, data.userId, idToken);
            if (isMounted) setIsFollowing(followingStatus);
          }
        }
      } catch (e) {
        if (isMounted) setErr(e.toString());
      }
    })();

    return () => { isMounted = false; };
  }, [portfolioId, currentUserId, idToken, checkIfFollowing, t]);

  // 시계열 데이터 로딩
  useEffect(() => {
    if (!portfolioId) return;
    let isMounted = true;

    fetch(`${API_BASE_URL}/portfolios/${portfolioId}/timeseries`)
      .then(res => res.ok ? res.json() : Promise.reject(res.status))
      .then(data => {
        if (!isMounted) return;
        const hasData = (data.cumulativeValues?.length > 0) || (data.compositionHistory?.length > 0);
        setIsNoData(!hasData);
        setTimeSeries(data);
        setLoading(false);
      })
      .catch(e => {
        if (isMounted) {
          setErr(e.toString());
          setLoading(false);
        }
      });
      
    return () => { isMounted = false; };
  }, [portfolioId]);

  // 팔로우 토글 핸들러
  const handleFollowToggle = async () => {
    if (!idToken) {
      toast.error(<LoginPromptToast signInRedirect={auth.signinRedirect} navigate={navigate} />);
      return;
    }
    try {
      const endpoint = `${API_BASE_URL}/user/follow`;
      const method = isFollowing ? 'DELETE' : 'POST';
      const res = await fetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify({ targetUserId: po.userId })
      });
      if (!res.ok) throw new Error('Follow action failed');
      setIsFollowing(!isFollowing);
    } catch (e) {
      console.error(e);
      //toast.error(t('portfolioDetail.followFailed'));
      toast.error(t('writePost.loginRequired')); // 에러에 맞춰서 로그인일때만 알림 나오게 수정
    }
  };

  // 리밸런싱 핸들러
  const handleRebalance = async (snapshots) => {
    try {
      const res = await fetch(`${API_BASE_URL}/portfolios/${portfolioId}/rebalance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth.user.access_token}` },
        body: JSON.stringify(snapshots)
      });
      if (!res.ok) throw new Error(res.status);
      const updated = await res.json();
      setPo(updated);
      setShowRebalanceModal(false);
    } catch (e) { console.error(e); }
  };

  return {
    portfolioId, po, timeSeries, err, loading, isNoData,
    isFollowing, showRebalanceModal, setShowRebalanceModal,
    handleFollowToggle, handleRebalance,
    currentUserId, navigate, DEFAULT_PROFILE_URL, API_BASE_URL
  };
};